import { useMemo } from "react";
import dayjs from "dayjs";
import { avgVideoConsumptionForDay } from "../utils/avgVideoConsumption";
import { averageActiveTv, ACTIVE_TV_DIVISOR } from "../utils/activeTv";

/* =========================
   HELPERS
========================= */

function buildShapeCurve() {
  const arr = new Array(1440);
  for (let i = 0; i < 1440; i++) {
    const angle = (2 * Math.PI * i) / 1440;
    arr[i] = (3.14 + Math.cos(angle + Math.PI / 2) * 0.5) * 5.5 * 0.95;
  }
  return arr;
}

function safe1440(arr) {
  if (!Array.isArray(arr)) return new Array(1440).fill(0);
  if (arr.length >= 1440) return arr.slice(0, 1440);

  const out = new Array(1440).fill(0);
  for (let i = 0; i < arr.length; i++) out[i] = Number(arr[i]) || 0;
  return out;
}

function sum(arr) {
  let t = 0;
  for (let i = 0; i < arr.length; i++) t += Number(arr[i]) || 0;
  return t;
}


function formatRange(arr, startMinute) {
  const first = arr.findIndex(v => v > 0);
  const last = arr.findLastIndex(v => v > 0);
  if (first === -1) return "0";

  const f = startMinute + first;
  const l = startMinute + last;

  const fh = String(Math.floor(f / 60)).padStart(2, "0");
  const fm = String(f % 60).padStart(2, "0");
  const lh = String(Math.floor(l / 60)).padStart(2, "0");
  const lm = String(l % 60).padStart(2, "0");

  return `${fh}:${fm} - ${lh}:${lm}`;
}

function getIsoWeekday(d) {
  const n = d.day(); 
  return n === 0 ? 7 : n;
}

// The unique TV endpoint answers { "date": count } and may be wrapped in .data
// depending on which hook fetched it.
function uniqueTvValue(uniqueTvData, selectedDate) {
  const u = uniqueTvData?.data ?? uniqueTvData;
  const key = findKeyByDate(u, selectedDate);
  return key ? Number(u[key]) || 0 : 0;
}

function findKeyByDate(obj, selectedDate) {
  if (!obj) return null;
  const target = dayjs(selectedDate).format("YYYY-MM-DD");

  return Object.keys(obj).find(k => {
    const d = dayjs(k, ["YYYY-MM-DD", "DD-MM-YYYY", "YYYY/MM/DD", dayjs.ISO_8601], true);
    if (!d.isValid()) return false;
    return d.format("YYYY-MM-DD") === target;
  });
}

/* =========================
   MAIN ENGINE
========================= */

export function useManagementReportEngine({
  selectedDate,
  rawImpressions,
  rawVideo,
  uniqueTvData,
  twitterData,
  youtubeData,
  epgBlocks,
}) {
  return useMemo(() => {
    if (!selectedDate || !rawImpressions || !epgBlocks?.length) {
      return {
        rows: [],
        sortedImpTop10: [],
        sortedSwitchTop10: [],
        sortedActiveTvTop10: [],
        totals: null,
        ready: false,
      };
    }

    const weekday = getIsoWeekday(dayjs(selectedDate));

    const dailyEpg = epgBlocks.filter(p => {
      // Block has an explicit date from the backend → require exact match
      if (p._raw?.key_date || p._raw?.Keydate) {
        return p.keyDate === selectedDate;
      }
      // Recurring weekly schedule (no explicit key_date) → match by weekday
      return p.weekdayInt === weekday;
    });

    if (!dailyEpg.length) {
      return {
        rows: [],
        sortedImpTop10: [],
        sortedSwitchTop10: [],
        sortedActiveTvTop10: [],
        totals: null,
        ready: false,
      };
    }

    const impKey = findKeyByDate(rawImpressions, selectedDate);
    const vidKey = findKeyByDate(rawVideo, selectedDate);
    const twKey  = findKeyByDate(twitterData, selectedDate);
    const ytKey  = findKeyByDate(youtubeData, selectedDate);

    const impArr = safe1440(rawImpressions?.[impKey]);
    const vidArr = safe1440(rawVideo?.[vidKey]);
    const twArr  = safe1440(twitterData?.[twKey]);
    const ytArr  = safe1440(youtubeData?.[ytKey]);

    const shape = buildShapeCurve();
    const shapedImp = impArr.map((v, i) => v * shape[i]);

    const usedTwitter = new Set();
    const usedYoutube = new Set();
    const rows = [];

    for (const p of dailyEpg) {
      let s = p.startMinute;
      let e = p.endMinute;
      if (e < s) e += 1440;
      if (s < 0 || s >= 1440) continue;

      const end = Math.min(e, 1440);

      const impSlice = shapedImp.slice(s, end);
      const rawSlice = impArr.slice(s, end);
      const vidSlice = vidArr.slice(s, end);
      const twSlice = twArr.slice(s, end);
      const ytSlice = ytArr.slice(s, end);

      let twitterSum = 0;
      let youtubeSum = 0;

      for (let i = s; i < end; i++) {
        if (!usedTwitter.has(i)) {
          usedTwitter.add(i);
          twitterSum += twArr[i] || 0;
        }
        if (!usedYoutube.has(i)) {
          usedYoutube.add(i);
          youtubeSum += ytArr[i] || 0;
        }
      }

      rows.push({
        saat: `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")} - ${String(Math.floor((end - 1) / 60)).padStart(2, "0")}:${String((end - 1) % 60).padStart(2, "0")}`,
        program: p.programName,
        type: end - s <= 5 ? "SPOT" : "NULL",
        totalvalue: Math.round(sum(impSlice)),
        value: Math.round(sum(rawSlice)),
        // Averaged over the slot rather than summed: active TV is a count of
        // sets at a moment, so adding the minutes together would answer a
        // question nobody asked.
        avgActiveTv: averageActiveTv(vidSlice),
        twittervalue: twitterSum,
        twitterTimeRange: formatRange(twSlice, s),
        youtubevalue: youtubeSum,
        youtubeTimeRange: formatRange(ytSlice, s),
      });
    }

    const sortedImpTop10 = [...rows]
      .filter(r => r.type !== "SPOT")
      .sort((a, b) => b.totalvalue - a.totalvalue)
      .slice(0, 10);

    const sortedSwitchTop10 = [...rows]
      .filter(r => r.type !== "SPOT")
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    const sortedActiveTvTop10 = [...rows]
      .filter(r => r.type !== "SPOT")
      .sort((a, b) => b.avgActiveTv - a.avgActiveTv)
      .slice(0, 10);

    const dailyImp = Math.round(sum(shapedImp));
    const dailyTwitter = sum(twArr);
    const dailyYoutube = sum(ytArr);

    // Both of these are computed from the day's own array rather than by
    // adding up the table rows. Summing the rows would double-count the
    // minutes two programmes share, and averaging a column of averages taken
    // over slots of different lengths is not a number anyone can use.
    const dailySwitchIn = sum(impArr);
    const dailyAvgActiveTv = averageActiveTv(vidArr);

    /* ✅ MONTHLY TOTALS */
    let monthlyImp = 0;
    let monthlyTwitter = 0;
    let monthlyYoutube = 0;

    let monthlySwitchIn = 0;
    let monthlyVideoTotal = 0;
    let monthlyVideoMinutes = 0;

    Object.values(rawImpressions || {}).forEach(arr => {
      const day = safe1440(arr);
      monthlyImp += sum(day);
      monthlySwitchIn += sum(day);
    });

    // Averaged across every minute of every day rather than across the daily
    // averages, so a partial day cannot weigh the same as a full one.
    Object.values(rawVideo || {}).forEach(arr => {
      const day = safe1440(arr);
      monthlyVideoTotal += sum(day);
      monthlyVideoMinutes += day.length;
    });

    Object.values(twitterData || {}).forEach(arr => {
      monthlyTwitter += sum(safe1440(arr));
    });

    Object.values(youtubeData || {}).forEach(arr => {
      monthlyYoutube += sum(safe1440(arr));
    });

    return {
      rows,
      sortedImpTop10,
      sortedSwitchTop10,
      sortedActiveTvTop10,
      totals: {
        daily: {
          imp: dailyImp,
          twitter: dailyTwitter,
          youtube: dailyYoutube,
          switchIn: dailySwitchIn,
          avgActiveTv: dailyAvgActiveTv,
          // Minutes per unique television across the whole day. It has no
          // per-programme equivalent — the figure only means anything divided
          // by a day's unique count — which is why it sits here rather than in
          // the table. null when the unique count is missing, so the report can
          // stay silent instead of printing a zero.
          avgVideoConsumption: avgVideoConsumptionForDay(
            vidArr,
            uniqueTvValue(uniqueTvData, selectedDate)
          ),
        },
        monthly: {
          imp: Math.round(monthlyImp),
          twitter: Math.round(monthlyTwitter),
          youtube: Math.round(monthlyYoutube),
          switchIn: Math.round(monthlySwitchIn),
          avgActiveTv: monthlyVideoMinutes
            ? Math.round(monthlyVideoTotal / ACTIVE_TV_DIVISOR / monthlyVideoMinutes)
            : 0,
        }
      },
      ready: true,
    };
  }, [selectedDate, rawImpressions, rawVideo, uniqueTvData, twitterData, youtubeData, epgBlocks]);
}
